import {
  DataGridBox,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import {
  CreateProductProjectPlanDialog,
  UpdateProductProjectPlanDialog,
  DeleteProductProjectPlanDialog,
} from './components';
import apiService from 'library/api-service';
import { renderDataGridLocaleText } from 'utils';
import { useSingleRowSelectionGuard } from 'hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { ProductProjectPlanItem } from 'library/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import { alpha, useTheme, Chip, Grid2 as Grid } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { PROJECT_STEP_PRIORITY_TYPE_ENUM, PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM } from 'enums';

const ProductProjectPlan = () => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const requireSingleRowSelection = useSingleRowSelectionGuard();
  const productProjectPlansRequestIdRef = useRef(0);

  // redux hooks
  const dispatch = useAppDispatch();
  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [productProjectPlans, setProductProjectPlans] = useState<ProductProjectPlanItem[]>([]);
  const [openCreateProductProjectPlanDialog, setOpenCreateProductProjectPlanDialog] = useState(false);
  const [openUpdateProductProjectPlanDialog, setOpenUpdateProductProjectPlanDialog] = useState(false);
  const [openDeleteProductProjectPlanDialog, setOpenDeleteProductProjectPlanDialog] = useState(false);

  // dynamic variables
  const getProjectStepPriorityTypeTranslated = useCallback(
    (projectStepPriorityType: number) =>
      ({
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.nonUrgent',
          defaultMessage: 'Düşük',
        }),
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.urgent',
          defaultMessage: 'Orta',
        }),
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.veryUrgent',
          defaultMessage: 'Yüksek',
        }),
      })[projectStepPriorityType],
    [intl]
  );

  const PRIORITY_OPTIONS = useMemo(
    () => [
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT),
      },
    ],
    [getProjectStepPriorityTypeTranslated]
  );

  const getProjectStepResponsibleContactTypeTranslated = useCallback(
    (projectStepResponsibleContactType: number) =>
      ({
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.customer',
          defaultMessage: 'Müşteri',
        }),
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.idecon',
          defaultMessage: 'Idecon',
        }),
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.customerAndIdecon',
          defaultMessage: 'Müşteri / Idecon',
        }),
      })[projectStepResponsibleContactType],
    [intl]
  );

  const RESPONSIBLE_CONTACT_OPTIONS = useMemo(
    () => [
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER,
        label: getProjectStepResponsibleContactTypeTranslated(PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER),
      },
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON,
        label: getProjectStepResponsibleContactTypeTranslated(PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON),
      },
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON,
        label: getProjectStepResponsibleContactTypeTranslated(
          PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON
        ),
      },
    ],
    [getProjectStepResponsibleContactTypeTranslated]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'projectId',
        headerName: intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => products.filter((project) => project?.id != null).map((project) => project.id),
        getOptionLabel: (value) => products.find((project) => String(project.id) === String(value))?.title ?? '',
        valueFormatter: (value) => products.find((project) => String(project.id) === String(value))?.title ?? '',
        minWidth: 200,
      },
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'projectStepManaging.projectStep', defaultMessage: 'Proje Adımı' }),
        minWidth: 250,
      },
      {
        field: 'description',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.stepExplanation',
          defaultMessage: 'Adım Açıklaması',
        }),
        minWidth: 250,
      },
      {
        field: 'estimatedTaskDay',
        headerName: intl.formatMessage({
          id: 'productProjectPlan.estimatedTaskDay',
          defaultMessage: 'Tahmini Adım Süresi (gün)',
        }),
        headerAlign: 'center',
        align: 'center',
        minWidth: 200,
      },
      {
        field: 'priority',
        headerName: intl.formatMessage({ id: 'projectStepManaging.priority', defaultMessage: 'Öncelik' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => PRIORITY_OPTIONS.map((x) => x.value),
        getOptionLabel: (value) => PRIORITY_OPTIONS.find((x) => x.value === value)?.label ?? String(value),
        valueFormatter: (value) => PRIORITY_OPTIONS.find((x) => x.value === value)?.label ?? '',
        renderCell: (params) => {
          const priority = params.value ?? params.row?.priority ?? PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT;
          const colorKey =
            priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT
              ? 'success'
              : priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT
                ? 'warning'
                : 'error';

          return (
            <Chip
              size="small"
              label={getProjectStepPriorityTypeTranslated(priority)}
              sx={{
                fontWeight: 600,
                bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                color: theme.palette[colorKey].main,
                border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
                cursor: 'default',
              }}
            />
          );
        },
        minWidth: 200,
      },
      {
        field: 'responsibleContactType',
        headerName: intl.formatMessage({ id: 'productProjectPlan.responsibleContact', defaultMessage: 'Sorumlu' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => RESPONSIBLE_CONTACT_OPTIONS.map((x) => x.value),
        getOptionLabel: (value) => RESPONSIBLE_CONTACT_OPTIONS.find((x) => x.value === value)?.label ?? String(value),
        valueFormatter: (value) => RESPONSIBLE_CONTACT_OPTIONS.find((x) => x.value === value)?.label ?? '',
        minWidth: 200,
      },
    ],
    [intl, products, theme, PRIORITY_OPTIONS, RESPONSIBLE_CONTACT_OPTIONS, getProjectStepPriorityTypeTranslated]
  );

  const handleCloseCreateProductProjectPlanDialog = () => setOpenCreateProductProjectPlanDialog(false);
  const handleClickOpenCreateProductProjectPlanDialog = () => setOpenCreateProductProjectPlanDialog(true);

  const handleCloseUpdateProductProjectPlanDialog = () => setOpenUpdateProductProjectPlanDialog(false);
  const handleClickOpenUpdateProductProjectPlanDialog = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    setOpenUpdateProductProjectPlanDialog(true);
  };

  const handleCloseDeleteProductProjectPlanDialog = () => setOpenDeleteProductProjectPlanDialog(false);
  const handleClickOpenDeleteProductProjectPlanDialog = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    setOpenDeleteProductProjectPlanDialog(true);
  };

  const getProductProjectPlans = useCallback(async () => {
    const requestId = ++productProjectPlansRequestIdRef.current;
    const postData: { projectId: number | null } = {
      projectId: null,
    };

    try {
      setLoading(true);

      const response = await apiService.getProductProjectPlans(postData);

      if (requestId === productProjectPlansRequestIdRef.current && response.isSuccess) {
        const sortedData = [...response.data].sort((a, b) =>
          (products.find((product) => product.id === a.projectId)?.title || '').localeCompare(
            products.find((product) => product.id === b.projectId)?.title || ''
          )
        );
        setProductProjectPlans(sortedData);
      }
    } catch {
    } finally {
      if (requestId === productProjectPlansRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [products]);

  useEffect(() => {
    getProductProjectPlans();

    return () => {
      productProjectPlansRequestIdRef.current += 1;
    };
  }, [getProductProjectPlans]);

  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-project-task-base']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2.5 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateProductProjectPlanDialog}>
              <FormattedMessage
                id="productProjectPlan.createProductProjectPlan"
                defaultMessage="Ürün - Proje Planı Oluştur"
              />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['update-project-task-base']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2.5 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateProductProjectPlanDialog} color="info">
              <FormattedMessage
                id="productProjectPlan.updateProductProjectPlan"
                defaultMessage="Ürün - Proje Planı Güncelle"
              />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['delete-project-task-base']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2.5 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteProductProjectPlanDialog}>
              <FormattedMessage
                id="productProjectPlan.deleteProductProjectPlan"
                defaultMessage="Ürün - Proje Planı Sil"
              />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>

      <br />

      <DataGridBox>
        <ZebraDataGrid
          pagination
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={productProjectPlans}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          slotProps={{
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
              },
            },
          }}
          disableRowGrouping
        />
      </DataGridBox>

      <CreateProductProjectPlanDialog
        open={openCreateProductProjectPlanDialog}
        handleClose={handleCloseCreateProductProjectPlanDialog}
        products={products}
        priorityOptions={PRIORITY_OPTIONS}
        responsibleContactOptions={RESPONSIBLE_CONTACT_OPTIONS}
        refresh={getProductProjectPlans}
      />
      <UpdateProductProjectPlanDialog
        open={openUpdateProductProjectPlanDialog}
        handleClose={handleCloseUpdateProductProjectPlanDialog}
        productProjectPlanId={rowSelectionModel[0]}
        products={products}
        priorityOptions={PRIORITY_OPTIONS}
        responsibleContactOptions={RESPONSIBLE_CONTACT_OPTIONS}
        refresh={getProductProjectPlans}
      />
      <DeleteProductProjectPlanDialog
        open={openDeleteProductProjectPlanDialog}
        productProjectPlanId={rowSelectionModel[0]}
        handleClose={handleCloseDeleteProductProjectPlanDialog}
        refresh={getProductProjectPlans}
        setRowSelectionModel={setRowSelectionModel}
      />
    </>
  );
};

export default ProductProjectPlan;
